import React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    SrgQuestionnaireTitle,
    SrgVerdictParagraph,
    StaticLinkWrapper,
    SrgParagraph,
    SrgQuestionnaireButton,
    SrgParagraphSecondary,
} from './SrgQuestionnaire.style';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { SrgVerdictFailedPrimary } from './srgQuestionnaire/srgVerdictVersions/srgVerdictPrimary';
import { SrgVerdictFailedSecondary } from './srgQuestionnaire/srgVerdictVersions/srgVerdictSecondary';

interface SrgVerdictPropsType {
    operatorFullName: string;
    srgPassed: boolean;
    languagesState: LanguagesState;
    handleCloseModal: () => void;
    srgQuestionnaireFailedVersion: 'primary' | 'secondary';
    accountHelperMail: string;
}

export interface SrgVerdictFailedPrimaryPropsType {
    operatorFullName: string;
    languagesState: LanguagesState;
    handleCloseModal: () => void;
}

export interface SrgVerdictFailedSecondaryPropsType {
    handleCloseModal: () => void;
    accountHelperMail: string;
}

export const SrgVerdict = observer(
    'SrgVerdict',
    ({
        operatorFullName,
        srgPassed = true,
        languagesState,
        handleCloseModal,
        srgQuestionnaireFailedVersion,
        accountHelperMail,
    }: SrgVerdictPropsType): JSX.Element | null => {
        return (
            <>
                <SrgQuestionnaireTitle data-test='srg-verdict-header'>
                    {srgPassed ? (
                        <I18n
                            langKey='srg.verdict.header.pass'
                            defaultText='Passed Customer Interaction Questionnaire'
                        />
                    ) : (
                        <I18n
                            langKey='srg.verdict.header.fail'
                            defaultText='Failed Customer Interaction Questionnaire'
                        />
                    )}
                </SrgQuestionnaireTitle>

                {srgPassed === true ? (
                    <SrgVerdictParagraph data-test='srg-verdict-paragraph'>
                        <SrgParagraph>
                            <I18n
                                langKey='srg.verdict.pass.part1'
                                defaultText='Thank you for taking the time to complete our responsible gambling interaction.'
                            />
                        </SrgParagraph>

                        <SrgParagraph>
                            <I18n
                                langKey='srg.verdict.pass.part2'
                                defaultText='For more information on how {operatorFullName} can help you, please follow this link:'
                                params={{ operatorFullName }}
                            />
                        </SrgParagraph>

                        <StaticLinkWrapper
                            isUnderlined={true}
                            to={{
                                account: 'static',
                                static: 'responsible-gambling',
                            }}
                        >
                            <I18n
                                langKey='srg.verdict.pass.part3'
                                defaultText='{operatorFullName} - Responsible Gambling'
                                params={{ operatorFullName }}
                            />
                        </StaticLinkWrapper>

                        <SrgParagraph>
                            <I18n
                                langKey='srg.verdict.pass.part4'
                                defaultText='If you ever feel your gambling is not fun anymore, please contact us via live chat or email to discuss, and one of our team will be available.'
                            />
                        </SrgParagraph>

                        <SrgParagraph>
                            <I18n
                                langKey='srg.verdict.pass.part5'
                                defaultText='Alternatively, visit one of the websites below for advice and help.'
                            />
                        </SrgParagraph>

                        <SrgParagraph>
                            <StaticLinkWrapper
                                isUnderlined={true}
                                to='https://www.gamcare.org.uk'
                            >
                                www.gamcare.org.uk
                            </StaticLinkWrapper>
                            <br />

                            <StaticLinkWrapper
                                isUnderlined={true}
                                to='https://www.gamblersanonymous.org.uk'
                            >
                                www.gamblersanonymous.org.uk
                            </StaticLinkWrapper>
                            <br />

                            <StaticLinkWrapper
                                isUnderlined={true}
                                to='https://www.gamstop.co.uk'
                            >
                                www.gamstop.co.uk
                            </StaticLinkWrapper>
                        </SrgParagraph>

                        <SrgParagraph>
                            <I18n
                                langKey='srg.verdict.pass.part6'
                                defaultText='{operatorFullName} can help customers with potential gambling problems by offering them the following services:'
                                params={{ operatorFullName }}
                            />
                        </SrgParagraph>

                        <SrgParagraph>
                            <I18n
                                langKey='srg.verdict.pass.part7'
                                defaultText='Deposit Limits: This gives the customer the ability to cap his/her spend over a period of time. Deposit limits can be set on a daily/weekly/monthly basis.'
                            />
                        </SrgParagraph>

                        <SrgParagraph>
                            <I18n
                                langKey='srg.verdict.pass.part8'
                                defaultText='Player Breaks: This gives the customer the ability to take a short break from their gambling experience for a small period of time. Daily, weekly or monthly breaks are available to our customers.'
                            />
                        </SrgParagraph>

                        <SrgParagraphSecondary>
                            <I18n
                                langKey='srg.verdict.pass.part9'
                                defaultText='Reality Checks: This is a notification that interrupts your session to discreetly remind you how long you have been playing.'
                            />
                        </SrgParagraphSecondary>

                        <SrgParagraph>
                            <I18n
                                langKey='srg.verdict.pass.part10'
                                defaultText='Self-exclusion: This service allows customers to take a long term/permanent break from their gambling experience. Customers have no access to their betting account and we will not contact them with marketing offers or promotions. Self-exclusion breaks can last from six months to up to 5 years. We can extend the exclusion at the customer{apostrophe}s request.'
                                params={{ apostrophe: "'" }}
                            />
                        </SrgParagraph>

                        <SrgQuestionnaireButton
                            size='large'
                            onClick={handleCloseModal}
                            dataTest='close-button'
                        >
                            Ok, I understand
                        </SrgQuestionnaireButton>
                    </SrgVerdictParagraph>
                ) : srgQuestionnaireFailedVersion === 'primary' ? (
                    <SrgVerdictFailedPrimary
                        operatorFullName={operatorFullName}
                        languagesState={languagesState}
                        handleCloseModal={handleCloseModal}
                    />
                ) : (
                    <SrgVerdictFailedSecondary
                        handleCloseModal={handleCloseModal}
                        accountHelperMail={accountHelperMail}
                    />
                )}
            </>
        );
    }
);
